import type { KpiSectionId } from "@nexus/contracts";

/**
 * Fixed sections for the Topic Detail / creative Performance grid (AD-2758).
 *
 * Catalog KPIs are placed by canonical `Kpi.name`. A name that is not listed —
 * a custom KPI with no saved section, or a tenant metric such as "Add To Cart"
 * — lands in Other. A user-created KPI carries its section on the entry.
 * Order inside a group is the caller's order (the user's saved index).
 */

export type KpiGroupId = KpiSectionId;

export interface KpiGroupDefinition {
  id: KpiGroupId;
  labelKey: string;
  defaultLabel: string;
  kpiNames: readonly string[];
}

export const KPI_GROUPS: readonly KpiGroupDefinition[] = [
  {
    id: "keyPerformance",
    labelKey: "kpiSettings.groups.keyPerformance",
    defaultLabel: "Key Performance",
    kpiNames: ["Spend", "ROAS", "CPA", "Conversions", "CVR", "CVR (C2P)"],
  },
  {
    id: "engagementCreative",
    labelKey: "kpiSettings.groups.engagementCreative",
    defaultLabel: "Engagement & Creative",
    kpiNames: ["Thumbstop", "CTR", "CR", "Stick Rate", "Engagement", "Holdrate", "Likes", "Comments"],
  },
  {
    id: "costEfficiency",
    labelKey: "kpiSettings.groups.costEfficiency",
    defaultLabel: "Cost & Efficiency",
    kpiNames: [
      "CPM",
      "CPC",
      "CPCheckout",
      "AOV",
      "Custom",
      "Spend/Creatives",
      "Spend/Sticked",
      "Frequency",
      "New Users %",
      "New users %",
    ],
  },
  {
    id: "scale",
    labelKey: "kpiSettings.groups.scale",
    defaultLabel: "Scale",
    kpiNames: ["Creatives", "Ads", "Spend %", "Creatives %"],
  },
  {
    id: "other",
    labelKey: "kpiSettings.groups.other",
    defaultLabel: "Other",
    kpiNames: [],
  },
];

const GROUP_ID_BY_NAME = new Map<string, KpiGroupId>(
  KPI_GROUPS.flatMap((group) => group.kpiNames.map((name) => [name, group.id] as const)),
);

export function getKpiGroupId(entry: {
  name: string;
  section?: KpiGroupId | null;
}): KpiGroupId {
  return entry.section ?? GROUP_ID_BY_NAME.get(entry.name) ?? "other";
}

export interface GroupedKpis<T> {
  id: KpiGroupId;
  labelKey: string;
  defaultLabel: string;
  entries: T[];
}

export interface GroupUserKpisOptions {
  /** Section order. Missing ids are appended in the default order. */
  order?: readonly KpiGroupId[];
}

const GROUP_BY_ID = new Map(KPI_GROUPS.map((group) => [group.id, group]));

/** Groups in `order` (default `KPI_GROUPS`). Entries keep their input order inside each group. */
export function groupUserKpis<T extends { name: string; section?: KpiGroupId | null }>(
  entries: readonly T[],
  options: GroupUserKpisOptions = {},
): GroupedKpis<T>[] {
  const order = normalizeGroupOrder(options.order);
  const buckets = new Map<KpiGroupId, T[]>(order.map((id) => [id, []]));

  for (const entry of entries) {
    const bucket = buckets.get(getKpiGroupId(entry));
    bucket?.push(entry);
  }

  return order.map((id) => {
    const group = GROUP_BY_ID.get(id);
    return {
      id,
      labelKey: group?.labelKey ?? id,
      defaultLabel: group?.defaultLabel ?? id,
      entries: buckets.get(id) ?? [],
    };
  });
}

function normalizeGroupOrder(order: readonly KpiGroupId[] | undefined): KpiGroupId[] {
  const seen = new Set<KpiGroupId>();
  const next: KpiGroupId[] = [];
  for (const id of order ?? []) {
    if (!GROUP_BY_ID.has(id) || seen.has(id)) continue;
    seen.add(id);
    next.push(id);
  }
  for (const group of KPI_GROUPS) {
    if (!seen.has(group.id)) next.push(group.id);
  }
  return next;
}
